import React, { ReactElement, memo, useCallback } from "react";

// constants
import { TEXTS } from "@cart-ui/constants";

// helpers
import { capitalizeFirstLetter } from "@swiggy-private/core-ui";
import { AddressInfo } from "@swiggy-private/cart-types";

//test ids
import { CART_FOOTER_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { getAddressAnnotation, hasInvalidAddressCategory } from "@cart-ui/helpers/CartFooter";

//components
import { Container, ContainerMeta, SubTitleText, MetaTextWrapper, ActionsSection, ActionButton } from "../styled";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as LocationFilledIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/locationFilled/20";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ADDRESS_LIST_SOURCE } from "@cart-ui/constants/Address";

//types and interfaces
export interface AddressSelectorFooterProps {
    addresses: AddressInfo[];
    selectAddress: VoidFunction;
    addNewAddress: ({ source }: { source?: string }) => void;
}

function AddressSelectorFooter(props: AddressSelectorFooterProps): ReactElement {
    const { addNewAddress } = props;
    const subTitle = (): string => {
        return props.addresses
            .map((address) => {
                const category =
                    hasInvalidAddressCategory(address) ? address.addressCategory : address?.userIntentAddressCategory;

                if (hasInvalidAddressCategory(address)) {
                    return getAddressAnnotation(address, category);
                }
                return address?.annotation || getAddressAnnotation(address, category);
            })
            .map((annotation) => capitalizeFirstLetter(annotation))
            .join(", ");
    };

    // Function added to handle address issues during MWEB cart migration
    const onAddAddressClick = useCallback(() => {
        addNewAddress({ source: ADDRESS_LIST_SOURCE.ADDRESS_LIST_FOOTER });
    }, [addNewAddress]);

    return (
        <Container>
            <ContainerMeta>
                <GenericIcon
                    Icon={LocationFilledIcon}
                    fillColor="primary"
                    strokeColor="primary"
                    data-testid={CART_FOOTER_TEST_IDS.ADDRESS_SELECTOR_ICON}
                />
                <MetaTextWrapper>
                    <Text variant="V2.subheading1.neutral.black" color="text_High_Emphasis">
                        {TEXTS.MULTIPLE_ADD_IN_LOCATION}
                    </Text>
                    <SubTitleText
                        variant="V2.body3.neutral.regular"
                        color="text_Med_Emphasis"
                        data-testid={CART_FOOTER_TEST_IDS.ADDRESS_SELECTOR_ANNOTATION}
                    >
                        {subTitle()}
                    </SubTitleText>
                </MetaTextWrapper>
            </ContainerMeta>
            <ActionsSection>
                <ActionButton
                    onClick={onAddAddressClick}
                    data-testid={CART_FOOTER_TEST_IDS.ADD_NEW_ADDRESS_BTN}
                    buttonType="secondary"
                    buttonSize="large"
                    variant="fullwidth"
                >
                    {TEXTS.ADD_ADDRESS}
                </ActionButton>
                <ActionButton
                    onClick={props.selectAddress}
                    data-testid={CART_FOOTER_TEST_IDS.SELECT_ADDRESS_BTN}
                    buttonType="primary"
                    buttonSize="large"
                    variant="fullwidth"
                >
                    {TEXTS.SELECT_ADDRESS}
                </ActionButton>
            </ActionsSection>
        </Container>
    );
}

export default memo(AddressSelectorFooter);
